/* MyBoost cart drawer additions — verified accounts, delivery rhythm, pack swap, re-check before checkout.
   assets/myboost-cart-drawer-v12.js is never modified: this module decorates the markup of snippets/cart-drawer.liquid
   and wraps drawer.onCartDrawerUpdate(). Pure helpers are exported for node --test. */

const API_PLATFORMS = ['instagram', 'tiktok']; // keep in sync with API_PLATFORMS in assets/mb-account-check.js (I-2)
const BLOCKING = ['private', 'not_found'];

export function lineFromDataset(data = {}) {
  return {
    line: Number.parseInt(data.line, 10) || 0,
    quantity: Number.parseInt(data.mbaQuantity, 10) || 1,
    variantId: Number.parseInt(data.variantId, 10) || 0,
    key: data.mbaKey || '',
    platform: data.mbaPlatform || '',
    account: data.mbaAccount || '',
    check: data.mbaCheck || '',
    sellingPlan: data.mbaSellingPlan || '',
    monthlyPlan: data.mbaMonthlyPlan || '',
    props: data.props || '',
    url: data.mbaUrl || '',
    usernameKey: data.usernameKey || '',
  };
}

export function linesNeedingCheck(lines) {
  return lines.filter((line) => line.check === 'verified' && API_PLATFORMS.includes(line.platform) && Boolean(line.account));
}

export function blockedCount(results) {
  return results.filter((result) => result && BLOCKING.includes(result.status)).length;
}

export function rhythmChangeBody(line, planId) {
  return { line: line.line, quantity: line.quantity, selling_plan: planId ? Number(planId) : null };
}

export function parseProps(value) {
  const properties = {};
  String(value || '').split(';;').forEach((pair) => {
    const [key, val] = pair.split('::');
    if (key && val !== undefined) properties[key] = val;
  });
  return properties;
}

// selection[i] = wanted value of option i + 1 ('' or a hole = any). profileIndex = 1-based profile-type option, 0 if none.
// Exact available variant first; otherwise the first available variant that ignores the profile-type option.
export function pickPackVariant({ variantsData, available, selection, profileIndex = 0 }) {
  const open = new Set(String(available || '').split(',').filter(Boolean));
  const rows = String(variantsData || '').split('||').filter(Boolean).map((entry) => {
    const [id, ...values] = entry.split('::');
    return { id, values };
  });
  const fits = (row, skip) => selection.every((value, index) => index === skip || !value || row.values[index] === value);
  const hit = rows.find((row) => open.has(row.id) && fits(row, -1))
    || (profileIndex ? rows.find((row) => open.has(row.id) && fits(row, profileIndex - 1)) : undefined);
  return hit ? Number.parseInt(hit.id, 10) : null;
}

// Add-then-remove-by-key (never by line index): the new variant is added first, and the old line is
// only dropped once the add has succeeded, identified by its stable cart item key so a concurrent
// change never removes the wrong line. If the add fails, the caller keeps the original line (I-1).
export function packSwapBodies(line, variantId) {
  const properties = parseProps(line.props);
  const add = { id: variantId, quantity: line.quantity };
  if (Object.keys(properties).length > 0) add.properties = properties;
  if (line.sellingPlan) add.selling_plan = Number(line.sellingPlan);
  return [add, { id: line.key, quantity: 0 }];
}

// Same add-then-remove-by-key shape as packSwapBodies, used when the buyer edits the account/link
// instead of the pack (same variant, new properties).
export function editSwapBodies(line, properties) {
  const add = { id: line.variantId, quantity: line.quantity, properties };
  if (line.sellingPlan) add.selling_plan = Number(line.sellingPlan);
  return [add, { id: line.key, quantity: 0 }];
}

// Route an inline edit: API platforms (instagram/tiktok) editing a profile handle go through the
// account-check relay; every post-link edit and every non-API platform go through shell.validateTarget.
export function editRoute(platform, targetType) {
  return (targetType !== 'link' && API_PLATFORMS.includes(platform)) ? 'account' : 'shell';
}

// Builds the properties object for an edited line: replaces the old visible key (whatever label it
// carried, including a legacy one) with the canonical key for the current target type, refreshes the
// hidden _mb_* triplet, and keeps every other property already on the line untouched.
export function editedProperties({ line, keys, targetType, value, check, account }) {
  const properties = parseProps(line.props);
  if (line.usernameKey) delete properties[line.usernameKey];
  const targetKey = targetType === 'link' ? keys.link : keys.profile;
  if (targetKey) properties[targetKey] = value;
  properties._mb_check = check;
  properties._mb_platform = line.platform;
  if (account) properties._mb_account = account;
  else delete properties._mb_account;
  return properties;
}

// B2: re-saving an unchanged target must not touch the network at all. Shopify's /cart/add.js merges
// an add with identical variant+properties into the existing line (same key, doubled quantity); the
// following remove-by-key would then delete that merged line outright. Order-independent, flat only
// (property values here are always strings written by this module).
export function sameProperties(a, b) {
  const left = a || {};
  const right = b || {};
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => Object.prototype.hasOwnProperty.call(right, key) && left[key] === right[key]);
}

export function formatMoney(cents, locale, currency) {
  const amount = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat(locale || 'fr', { style: 'currency', currency: currency || 'EUR' }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || ''}`.trim();
  }
}

// Avatar images render at native size without a proxy: only accept https URLs on the same origin as
// the configured relay, so a compromised or misconfigured relay response can't point the <img> at an
// arbitrary origin.
export function isSafeAvatarUrl(url, relayBase) {
  if (typeof url !== 'string' || !url) return false;
  let target;
  let relay;
  try { target = new URL(url); } catch { return false; }
  if (target.protocol !== 'https:') return false;
  try { relay = new URL(relayBase); } catch { return false; }
  return target.origin === relay.origin;
}

// A stale public/private/not_found cache entry (module.CACHE_KEY, I-2) survives a relay outage since
// writeCache never stores 'unavailable' results; drop it explicitly so a later, working check isn't
// shadowed by yesterday's answer.
export function forgetCache(module, store, platform, account) {
  if (!module || !store || !platform || !account) return;
  try {
    const raw = store.getItem(module.CACHE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    const key = `${platform}:${account}`;
    if (key in data) {
      delete data[key];
      store.setItem(module.CACHE_KEY, JSON.stringify(data));
    }
  } catch {
    /* corrupt cache: leave it for readCache's own guard to discard */
  }
}

export function wrapUpdate(target, after) {
  const wrap = (fn) => {
    if (typeof fn !== 'function' || fn.mbaWrapped) return fn;
    const wrapped = async function onCartDrawerUpdate(...args) {
      try {
        return await fn.apply(this, args);
      } finally {
        after();
      }
    };
    wrapped.mbaWrapped = true;
    return wrapped;
  };
  let current = wrap(target.onCartDrawerUpdate);
  Object.defineProperty(target, 'onCartDrawerUpdate', {
    configurable: true,
    enumerable: true,
    get: () => current,
    set: (fn) => { current = wrap(fn); },
  });
  return target;
}

export function shouldIntercept({ checkedFlag, needing }) {
  if (checkedFlag === '1') return false;
  const count = Array.isArray(needing) ? needing.length : Number(needing) || 0;
  return count > 0;
}

export function init(drawer) {
  if (drawer.dataset.mbaCartReady) return;
  drawer.dataset.mbaCartReady = '1';
  const i18n = document.getElementById('mbCartI18n')?.dataset || {};
  const statusText = { public: i18n.mbaStatusPublic, unverified: i18n.mbaStatusUnverified, private: i18n.mbaStatusPrivate };
  const root = window.Shopify?.routes?.root || '/';
  let accounts = null;
  let shell = null;
  let busy = false;

  const lineEls = () => [...drawer.querySelectorAll('.mb-item[data-line]')];
  const needsCheck = (el) => linesNeedingCheck([lineFromDataset(el.dataset)]).length > 0;
  const storage = () => {
    try { return window.sessionStorage; } catch { return null; }
  };
  const loadAccounts = () => {
    accounts ||= import(drawer.dataset.mbaAccountCheckUrl)
      .then((module) => ({ module, checker: module.createChecker({ baseUrl: drawer.dataset.mbaRelay || '' }) }))
      .catch(() => null);
    return accounts;
  };
  const loadShell = () => {
    shell ||= import(drawer.dataset.mbaShellUrl).then((module) => ({ module })).catch(() => null);
    return shell;
  };

  function setAvatar(el, result) {
    const slot = el.querySelector('[data-mba-avatar]');
    const fallback = el.querySelector('[data-mba-line-img]');
    if (!slot || !result?.avatar || slot.firstChild) return;
    if (!isSafeAvatarUrl(result.avatar, drawer.dataset.mbaRelay || '')) return;
    const img = new Image(56, 56);
    img.alt = '';
    img.decoding = 'async';
    img.referrerPolicy = 'no-referrer';
    img.addEventListener('error', () => {
      slot.replaceChildren();
      slot.hidden = true;
      if (fallback) fallback.hidden = false;
    }, { once: true });
    img.src = result.avatar;
    slot.append(img);
    slot.hidden = false;
    if (fallback) fallback.hidden = true;
  }

  function clearPill(el) {
    const pill = el.querySelector('[data-mba-status]');
    if (!pill) return;
    pill.dataset.mbaStatus = '';
    pill.className = 'mba-pill';
    pill.textContent = '';
    pill.hidden = true;
  }

  function applyResult(el, result) {
    const status = result?.status;
    if (status !== 'public' && !BLOCKING.includes(status)) {
      el.removeAttribute('data-mba-line-blocked');
      const alert = el.querySelector('[data-mba-alert]');
      if (alert) alert.hidden = true;
      clearPill(el);
      return;
    }
    const blocked = BLOCKING.includes(status);
    const key = blocked ? 'private' : 'public';
    const pill = el.querySelector('[data-mba-status]');
    if (pill) {
      pill.dataset.mbaStatus = key;
      pill.className = `mba-pill mba-pill--${key}`;
      pill.textContent = statusText[key] || '';
      pill.hidden = false;
    }
    const alert = el.querySelector('[data-mba-alert]');
    if (alert) alert.hidden = !blocked;
    el.toggleAttribute('data-mba-line-blocked', blocked);
  }

  function refreshBlocked() {
    const count = drawer.querySelectorAll('[data-mba-line-blocked]').length;
    const note = drawer.querySelector('[data-mba-cart-blocked]');
    if (note) {
      note.textContent = count ? (i18n.mbaBlocked || '').replace('{count}', String(count)) : '';
      note.hidden = count === 0;
    }
    const button = drawer.querySelector('.mb-checkout');
    if (!button) return;
    if (count) {
      button.setAttribute('data-mba-blocked', '');
      button.disabled = true;
    } else if (button.hasAttribute('data-mba-blocked')) {
      button.removeAttribute('data-mba-blocked');
      button.disabled = (Number.parseInt(drawer.dataset.cartCount, 10) || 0) === 0 || button.hasAttribute('data-mb-min-disabled');
    }
  }

  async function decorate() {
    const pending = lineEls().filter(needsCheck);
    if (pending.length) {
      const loaded = await loadAccounts();
      const store = storage();
      if (loaded && store) {
        pending.forEach((el) => {
          const line = lineFromDataset(el.dataset);
          const cached = loaded.module.readCache(store, line.platform, line.account);
          if (!cached) return;
          setAvatar(el, cached);
          applyResult(el, cached);
        });
      }
    }
    refreshBlocked();
  }

  async function post(path, body) {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`${path} ${response.status}`);
    return response.json().catch(() => null);
  }

  // Returns whether `task` actually ran to completion: false when another run() is already busy (task
  // never starts) or when task threw (caller can then keep its own UI state instead of assuming success).
  async function run(task) {
    if (busy) return false;
    busy = true;
    drawer.querySelector('[data-loading]')?.classList.add('active');
    let ok = true;
    try {
      await task();
    } catch (error) {
      ok = false;
      console.error('[mb-cart]', error);
    }
    busy = false;
    if (typeof drawer.onCartDrawerUpdate === 'function') await drawer.onCartDrawerUpdate();
    else drawer.querySelector('[data-loading]')?.classList.remove('active');
    return ok;
  }

  function changeRhythm(button) {
    const el = button.closest('[data-line]');
    if (!el) return;
    const line = lineFromDataset(el.dataset);
    const planId = button.dataset.mbaRhythm === 'monthly' ? line.monthlyPlan : '';
    if (String(planId) === String(line.sellingPlan)) return;
    run(() => post('cart/change.js', rhythmChangeBody(line, planId)));
  }

  async function recheck(button) {
    const el = button.closest('[data-line]');
    if (!el) return;
    button.disabled = true;
    try {
      const loaded = await loadAccounts();
      if (!loaded) return; // relay module failed to load: leave the line blocked, button re-enables below
      const line = lineFromDataset(el.dataset);
      const result = await loaded.checker.profile(line.platform, line.account, { fresh: true });
      if (result?.status === 'unavailable') forgetCache(loaded.module, storage(), line.platform, line.account);
      setAvatar(el, result);
      applyResult(el, result);
      refreshBlocked();
    } finally {
      button.disabled = false;
    }
  }

  function showEditError(el, message) {
    const box = el.querySelector('[data-mba-edit-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  function openEdit(el) {
    if (!el) return;
    const row = el.querySelector('[data-mba-edit-row]');
    const trigger = el.querySelector('[data-mba-edit-btn]');
    if (!row) return;
    showEditError(el, '');
    row.hidden = false;
    if (trigger) trigger.hidden = true;
    const input = row.querySelector('[data-mba-edit-input]');
    if (input) {
      input.focus();
      input.select?.();
    }
  }

  function closeEdit(el) {
    if (!el) return;
    const row = el.querySelector('[data-mba-edit-row]');
    const trigger = el.querySelector('[data-mba-edit-btn]');
    showEditError(el, '');
    if (row) row.hidden = true;
    if (trigger) trigger.hidden = false;
  }

  // Save = add the edited variant/properties first, then remove the old line by key (I-1): never lose
  // the line on failure. API platforms editing a profile handle re-verify through the relay; every
  // post-link edit and every non-API platform validate the link shape only (assets/mb-shell.js).
  async function saveEdit(el) {
    if (!el) return;
    const input = el.querySelector('[data-mba-edit-input]');
    const saveBtn = el.querySelector('[data-mba-edit-save]');
    if (!input) return;
    showEditError(el, '');
    const raw = input.value;
    const line = lineFromDataset(el.dataset);
    const targetType = el.dataset.mbaTargetType || 'profile';
    const route = editRoute(line.platform, targetType);
    if (saveBtn) saveBtn.disabled = true;
    try {
      let outcome;
      let accountResult = null; // account route's fresh profile() result, needed below if the save turns out unchanged
      if (route === 'account') {
        const loaded = await loadAccounts();
        if (!loaded) {
          showEditError(el, i18n.mbaEditError || '');
          return;
        }
        const handle = loaded.module.parseHandle(line.platform, raw);
        if (!handle || !loaded.module.isValidHandle(line.platform, handle)) {
          window.alert(i18n.mbaInvalidAccount || i18n.alertLink || '');
          return;
        }
        const result = await loaded.checker.profile(line.platform, handle, { fresh: true });
        accountResult = result;
        if (result.status === 'unavailable') forgetCache(loaded.module, storage(), line.platform, handle);
        if (result.status === 'private' || result.status === 'not_found' || result.status === 'invalid') {
          setAvatar(el, result);
          applyResult(el, { ...result, status: 'private' });
          refreshBlocked();
          return; // do not save: the existing private/not-found alert now shows on the line
        }
        // account route only ever runs for profile-type lines (editRoute), and I-4 reserves _mb_account
        // for post products on an API platform: the visible key already carries the handle here, so
        // editedProperties must not add a redundant _mb_account (it would also make an unchanged save
        // look different from the line's existing properties, defeating the B2 "skip if unchanged" guard).
        outcome = { value: handle, check: result.status === 'public' ? 'verified' : 'unverified', account: '' };
      } else {
        const loaded = await loadShell();
        if (!loaded) {
          showEditError(el, i18n.mbaEditError || '');
          return;
        }
        const linkType = targetType === 'link' ? 'post' : 'profile';
        const result = loaded.module.validateTarget(raw, linkType, line.platform);
        if (!result.ok) {
          window.alert(i18n.alertLink || '');
          return;
        }
        // B1: an API-platform post line keeps its existing check (verified/unverified), not a fresh
        // 'format' — resetting it to 'format' would make linesNeedingCheck skip the line forever after,
        // silently unblocking a checkout that should still be re-verified (e.g. an account gone private).
        const keepAccount = targetType === 'link' && API_PLATFORMS.includes(line.platform) ? line.account : '';
        const check = keepAccount ? (line.check || 'format') : 'format';
        outcome = { value: result.value, check, account: keepAccount };
      }
      const properties = editedProperties({
        line,
        keys: { profile: i18n.defaultUsernameKey || '', link: i18n.propLinkKey || '' },
        targetType,
        value: outcome.value,
        check: outcome.check,
        account: outcome.account,
      });
      // B2: nothing actually changed (e.g. the buyer reopened the row and saved without editing it) ->
      // skip the network entirely. Adding an identical variant+properties merges into this very line
      // (same key, doubled quantity) on Shopify's side, and the remove-by-key step below would then
      // delete that merged line outright.
      if (sameProperties(properties, parseProps(line.props))) {
        // The handle itself didn't change, but the fresh check above may have: a line that was private
        // when the row was opened can be public now, and must unblock immediately, not only on the next
        // recheck/checkout attempt.
        if (accountResult) {
          setAvatar(el, accountResult);
          applyResult(el, accountResult);
          refreshBlocked();
        }
        closeEdit(el);
        return;
      }
      const [add, remove] = editSwapBodies(line, properties);
      const ok = await run(async () => {
        const added = await post('cart/add.js', add);
        if (added && added.key === line.key) {
          // Safety net: Shopify merged the add into the same line anyway (some property shape our own
          // sameProperties() check didn't catch as identical). Repair the quantity instead of deleting
          // the line with quantity: 0.
          await post('cart/change.js', { id: line.key, quantity: line.quantity });
        } else {
          await post('cart/change.js', remove);
        }
      });
      if (ok) closeEdit(el);
      else showEditError(el, i18n.mbaEditError || '');
    } finally {
      if (saveBtn) saveBtn.disabled = false;
    }
  }

  // Pack change, every line: v12 would drop the selling plan, ignore availability and alert on a missing
  // profile/quantity combination. Keeps properties + plan and falls back past the hidden profile-type
  // option. Add-then-remove-by-key (I-1): the new variant is added first; the old line is only dropped
  // (by its stable item key) once that add succeeds, so a failed add never loses the line.
  drawer.addEventListener('click', (event) => {
    const option = event.target.closest('.mb-dropdown-item');
    const el = option?.closest('[data-line]');
    if (!el) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const container = option.closest('.mb-var');
    container?.classList.remove('open');
    if (option.disabled || option.getAttribute('aria-disabled') === 'true') return;
    if (!container || option.dataset.value === container.dataset.current) return;
    const selection = [];
    el.querySelectorAll('.mb-var[data-var-index]').forEach((box) => {
      selection[Number(box.dataset.varIndex) - 1] = box.dataset.current || '';
    });
    selection[Number(container.dataset.varIndex) - 1] = option.dataset.value;
    const variantId = pickPackVariant({
      variantsData: el.querySelector('[data-var-index="1"]')?.dataset.variants,
      available: el.dataset.mbaAvailable,
      selection,
      profileIndex: Number.parseInt(el.dataset.mbaProfileIndex, 10) || 0,
    });
    if (!variantId) {
      window.alert(i18n.alertCombo || '');
      return;
    }
    const [add, remove] = packSwapBodies(lineFromDataset(el.dataset), variantId);
    run(async () => {
      try {
        await post('cart/add.js', add);
      } catch (error) {
        window.alert(i18n.alertCombo || '');
        throw error; // original line untouched: the remove step below never runs
      }
      await post('cart/change.js', remove);
    });
  }, { capture: true });

  drawer.addEventListener('click', (event) => {
    const rhythm = event.target.closest('[data-mba-rhythm]');
    if (rhythm) {
      changeRhythm(rhythm);
      return;
    }
    const again = event.target.closest('[data-mba-recheck]');
    if (again) {
      recheck(again);
      return;
    }
    if (event.target.closest('[data-mba-change-account]') && typeof drawer.close === 'function') {
      drawer.close();
      return;
    }
    const editBtn = event.target.closest('[data-mba-edit-btn]');
    if (editBtn) {
      openEdit(editBtn.closest('[data-line]'));
      return;
    }
    const cancelBtn = event.target.closest('[data-mba-edit-cancel]');
    if (cancelBtn) {
      closeEdit(cancelBtn.closest('[data-line]'));
      return;
    }
    const saveBtn = event.target.closest('[data-mba-edit-save]');
    if (saveBtn) saveEdit(saveBtn.closest('[data-line]'));
  });

  drawer.addEventListener('keydown', (event) => {
    const input = event.target.closest?.('[data-mba-edit-input]');
    if (!input) return;
    const el = input.closest('[data-line]');
    if (event.key === 'Enter') {
      event.preventDefault();
      saveEdit(el);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closeEdit(el);
    }
  });

  // Re-check verified Instagram/TikTok accounts before v12 sends the buyer to checkout (spec §7.4).
  // Registered on `window`, not the drawer: layout/theme.liquid's TgdShim listens on `document` in
  // capture phase and stops propagation for button[name="checkout"] (our .mb-checkout matches its
  // xpath). Capture phase runs window -> document -> ... -> target, so only a window-level listener is
  // guaranteed to run before TgdShim can swallow the click.
  let checking = false;
  window.addEventListener('click', async (event) => {
    const button = event.target.closest?.('#MinimogCartDrawer .mb-checkout');
    if (!button) return;
    const pending = lineEls().filter(needsCheck);
    if (!shouldIntercept({ checkedFlag: button.dataset.mbaChecked, needing: pending })) {
      delete button.dataset.mbaChecked;
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checking) return;
    checking = true;
    button.classList.add('loading');
    const loaded = await loadAccounts();
    const store = storage();
    const results = await Promise.all(pending.map((el) => {
      if (!loaded) return { status: 'unavailable' };
      const line = lineFromDataset(el.dataset);
      return loaded.checker.profile(line.platform, line.account).catch(() => ({ status: 'unavailable' }));
    }));
    pending.forEach((el, index) => {
      const result = results[index];
      const line = lineFromDataset(el.dataset);
      if (result?.status === 'unavailable' && loaded) forgetCache(loaded.module, store, line.platform, line.account);
      setAvatar(el, result);
      applyResult(el, result);
    });
    button.classList.remove('loading');
    checking = false;
    refreshBlocked();
    if (blockedCount(results) > 0) {
      if (!drawer.classList.contains('mb-open') && typeof drawer.open === 'function') drawer.open();
      drawer.querySelector('[data-mba-line-blocked]')?.scrollIntoView?.({ block: 'nearest' });
      return;
    }
    if (button.disabled) return;
    button.dataset.mbaChecked = '1';
    button.click();
    delete button.dataset.mbaChecked;
  }, { capture: true });

  // The cart footer shows one price, the total, refreshed by myboost-cart-drawer-v12's
  // updatePriceDisplay() (it also handles the promo code and the struck-through old price).
  wrapUpdate(drawer, () => {
    decorate().catch((error) => console.error('[mb-cart]', error));
  });
  decorate().catch((error) => console.error('[mb-cart]', error));
}

function start() {
  const drawer = document.getElementById('MinimogCartDrawer');
  if (drawer) init(drawer);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}
